// @ts-nocheck
/* Starter Sync - QuickOrderProductList */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearProducts from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ClearProducts";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { LinkPresentationProps } from "@insite/mobius/Link";
import CartCheckmarkFill from "@store/Library/Components/Toolkit/Icons/CartCheckmark/CartCheckmarkFill";
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import Upload from "@store/Library/Components/Toolkit/Icons/Upload";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
/* Starter Custom Code - End */

const enum fields {
    useOverflowMenu = "useOverflowMenu",
    hideForEmptyProductList = "hideForEmptyProductList",
    // Starter Custom Code
    showUpload = "showUpload",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.useOverflowMenu]: boolean;
        [fields.hideForEmptyProductList]: boolean;
        // Starter Custom Code
        [fields.showUpload]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        productInfos: state.pages.quickOrder.productInfos,
        products: state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value),
        orderUploadPageLink: getPageLinkByPageType(state, "OrderUploadPage"),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        allowMultipleWishLists: settingsCollection.wishListSettings.allowMultipleWishLists,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    clearProducts,
    loadCurrentCart,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface QuickOrderActionsStyles {
    wrapper?: InjectableCss;
    buttonsHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addAllToCartButton?: ButtonPresentationProps;
    addToListButton?: ButtonPresentationProps;
    uploadOrderButton?: ButtonPresentationProps;
    // Starter Custom Code
    uploadOrderLink?: LinkPresentationProps;
    addAllToCartLink?: LinkPresentationProps;
    addToListLink?: LinkPresentationProps;
}

export const actionsStyles: QuickOrderActionsStyles = {
    addAllToCartButton: { variant: "primary" },
    addAllToCartLink: { icon: { iconProps: { src: CartCheckmarkFill } } },
    addToListButton: { variant: "tertiary" },
    addToListLink: { icon: { iconProps: { src: PlusCircleFill } } },
    uploadOrderButton: { variant: "tertiary" },
    uploadOrderLink: { icon: { iconProps: { src: Upload } } },
};

const styles = actionsStyles;

const QuickOrderActions = ({
    fields: widgetFields, // Starter Custom Code
    productInfos,
    products,
    history,
    orderUploadPageLink,
    cartPageLink,
    showAddToCartConfirmationDialog,
    canOrderUpload,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    addCartLines,
    clearProducts,
    loadCurrentCart,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const trackAddToCart = ga.use.trackAddToCart(); // Starter Custom Code
    const trackAddAllItemsAndCheckout = ga.use.trackAddAllItemsAndCheckout(); // Starter Custom Code

    const [allQtysIsValid, setAllQtysIsValid] = useState(false);
    const productsExist = productInfos && productInfos.length > 0;

    useEffect(() => {
        setAllQtysIsValid(productInfos.every(o => o.qtyOrdered > 0));
    }, [productInfos]);

    if (widgetFields.hideForEmptyProductList && !productsExist /* Starter Custom Code */) {
        return null;
    }

    const uploadOrderClickHandler = () => {
        orderUploadPageLink && history.push(orderUploadPageLink.url);
    };

    const addAllToCartClickHandler = async () => {
        const reversedList = [...productInfos].reverse();
        const cartLinesCollection = (await addCartLines({ productInfos: reversedList })) as CartLineCollectionModel;

        if (showAddToCartConfirmationDialog) {
            const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                messageType: "success",
            });
        }

        /* Starter Custom Code - Start */
        trackAddAllItemsAndCheckout();
        trackAddToCart({ items: cartLinesCollection.cartLines }, { productInfos: reversedList });
        /* Starter Custom Code - End */

        clearProducts();
        cartPageLink && history.push(cartPageLink.url);
        loadCurrentCart({ shouldLoadFullCart: true });
    };

    // Starter Moved - addToListClickHandler - QuickOrderProductList

    // Starter Moved - canAddSomeToList - QuickOrderProductList

    /* Starter Custom Code - Start */
    return (
        <OrderActionSet
            fields={widgetFields}
            actions={[
                {
                    key: "cart",
                    label: translate("Place Order"),
                    onClick: addAllToCartClickHandler,
                    isDisabled: !allQtysIsValid,
                    buttonStyles: styles.addAllToCartButton,
                    linkStyles: styles.addAllToCartLink,
                    testSelector: "quickOrder_addAllProductsToCart",
                    isVisible: productsExist,
                    field: fields.showAddToCart,
                },
                {
                    key: "upload",
                    label: translate("Upload Order"),
                    onClick: uploadOrderClickHandler,
                    href: orderUploadPageLink?.url,
                    isDisabled: !allQtysIsValid,
                    buttonStyles: styles.uploadOrderButton,
                    linkStyles: styles.uploadOrderLink,
                    testSelector: "quickOrder_UploadOrder",
                    isVisible: canOrderUpload,
                    field: fields.showUpload,
                },
            ]}
        />
    );
    /* Starter Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(QuickOrderActions)),
    definition: {
        group: "Quick Order",
        allowedContexts: ["QuickOrderPage"],
        displayName: "Actions",
        fieldDefinitions: [
            // Starter Removed Code - useOverflowMenu
            {
                name: fields.hideForEmptyProductList,
                displayName: "Hide if Product List is Empty",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            /* Starter Custom Code - Start */
            ...orderActionSetFieldDefinitions([
                { name: fields.showAddToCart, defaultState: "sticky" },
                { name: fields.showUpload },
            ]),
            /* Starter Custom Code - End */
        ],
    },
};

export default widgetModule;
